/**
 * @description
 * Workspace manager tab component for managing workspaces and client assignments.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-06
 */

import type { WorkspaceManagerProps } from "./types";

export function WorkspaceManager({
  workspaces,
  clients,
  showCreateWorkspace,
  newWorkspaceName,
  assigningClients,
  selectedClients,
  loading: _loading,
  onRefresh,
  onCreateWorkspace,
  onArchiveWorkspace,
  onAssignClients,
  onShowCreateWorkspace,
  onSetNewWorkspaceName,
  onSetAssigningClients,
  onSetSelectedClients,
}: WorkspaceManagerProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Workspaces</h2>
        <div className="flex gap-2">
          <button
            onClick={onRefresh}
            className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
          >
            Refresh
          </button>
          <button
            onClick={() => onShowCreateWorkspace(true)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Create Workspace
          </button>
        </div>
      </div>

      {showCreateWorkspace && (
        <div className="rounded-md border bg-muted p-4">
          <h3 className="mb-3 font-medium">Create New Workspace</h3>
          <div className="flex gap-2">
            <input
              type="text"
              value={newWorkspaceName}
              onChange={(e) => onSetNewWorkspaceName(e.target.value)}
              placeholder="Workspace name"
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <button
              onClick={onCreateWorkspace}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              Create
            </button>
            <button
              onClick={() => {
                onShowCreateWorkspace(false);
                onSetNewWorkspaceName("");
              }}
              className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {workspaces.map((workspace) => (
          <div key={workspace.id} className="rounded-md border p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium">{workspace.name}</h3>
                  {workspace.displayColor && (
                    <div
                      className="h-4 w-4 rounded-full"
                      style={{ backgroundColor: workspace.displayColor }}
                    />
                  )}
                  {workspace.archivedAt && (
                    <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-800">
                      Archived
                    </span>
                  )}
                </div>
                <p className="mt-1 text-sm text-muted-foreground font-mono">
                  ID: {workspace.anthropicId}
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {workspace.clientIds.length} client(s) assigned
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onSetAssigningClients(workspace.id);
                    onSetSelectedClients(workspace.clientIds);
                  }}
                  className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium transition-colors hover:bg-accent"
                >
                  Assign Clients
                </button>
                {!workspace.archivedAt && (
                  <button
                    onClick={() => onArchiveWorkspace(workspace.id)}
                    className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                  >
                    Archive
                  </button>
                )}
              </div>
            </div>

            {assigningClients === workspace.id && (
              <div className="mt-4 rounded-md border bg-muted p-3">
                <h4 className="mb-2 text-sm font-medium">
                  Assign Clients
                </h4>
                <div className="mb-3 max-h-48 space-y-1 overflow-y-auto">
                  {clients.map((client) => (
                    <label
                      key={client.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            onSetSelectedClients([
                              ...selectedClients,
                              client.id,
                            ]);
                          } else {
                            onSetSelectedClients(
                              selectedClients.filter(
                                (id) => id !== client.id,
                              ),
                            );
                          }
                        }}
                        className="rounded border-gray-300"
                      />
                      {client.name} ({client.subdomain})
                    </label>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onAssignClients(workspace.id)}
                    className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      onSetAssigningClients(null);
                      onSetSelectedClients([]);
                    }}
                    className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium transition-colors hover:bg-accent"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
